import React, { useState } from 'react';
import { Navigation } from './Navigation';
import { useAuth } from '../contexts/AuthContext';
import { fileService } from '../services/fileService';
import './Dashboard.css';
import './NetWorth.css';

export const NetWorth: React.FC = () => {
  const { token } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [assets, setAssets] = useState('');
  const [liabilities, setLiabilities] = useState('');

  const parseVal = (v: string) => {
    const n = parseFloat(v.replace(/,/g, ''));
    return isNaN(n) ? 0 : Math.max(n, 0);
  };

  const assetsNum = parseVal(assets);
  const liabilitiesNum = parseVal(liabilities);
  const netWorth = assetsNum - liabilitiesNum;
  const hasInput = assets !== '' || liabilities !== '';

  const formatCurrency = (n: number) =>
    n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const handleDownload = async () => {
    if (!token) return;
    setDownloading(true);
    setError(null);
    try {
      const url = await fileService.getFileUrl(token, 'net-worth-template.xlsx');
      window.location.href = url;
    } catch (err) {
      setError('Failed to download file. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleWatchVideo = async () => {
    if (!token) return;
    setError(null);
    try {
      const url = await fileService.getFileUrl(token, 'networth-demo.mp4');
      setVideoUrl(url);
      setPlayingVideo(true);
    } catch (err) {
      setError('Failed to load video. Please try again.');
    }
  };

  return (
    <div className="dashboard-container">
      <Navigation />
      <div className="dashboard-content">

        <div className="welcome-section" style={{ marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>Net Worth Tracker</h2>
        </div>

        {/* 1. Think — motivate the reader */}
        <div className="nw-intro-card">
          <p className="nw-intro-lead">Most people think about money in fragments: income, savings, investments, debt. <strong>Net worth brings those pieces together into a single, coherent view.</strong></p>
          <p>Rather than focusing on perfection or optimization, this tracker emphasizes <strong>clarity and direction.</strong> By recording assets and liabilities in one place, patterns become visible that are often missed when accounts are viewed in isolation.</p>
          <p>What matters is how this number changes over time. <strong>Tracking quarterly or annually reveals whether progress is accelerating, stalling, or reversing</strong> — and which components are responsible.</p>
        </div>

        {/* 2. Calculate — quick interactive calculator */}
        <h3 className="nw-section-label">See Where You Stand</h3>
        <div className="nw-calculator-card">
          <div className="nw-calc-row">
            <div className="nw-calc-field">
              <label style={{ color: '#4ade80' }}>Total Assets</label>
              <div className="nw-input-wrapper">
                <span className="nw-dollar-sign">$</span>
                <input
                  type="number"
                  min="1"
                  placeholder="0"
                  value={assets}
                  onChange={e => {
                    const v = e.target.value;
                    if (v === '' || parseFloat(v) >= 1) setAssets(v);
                  }}
                />
              </div>
            </div>
            <span className="nw-calc-minus">−</span>
            <div className="nw-calc-field">
              <label style={{ color: '#f87171' }}>Total Liabilities</label>
              <div className="nw-input-wrapper">
                <span className="nw-dollar-sign">$</span>
                <input
                  type="number"
                  min="1"
                  placeholder="0"
                  value={liabilities}
                  onChange={e => {
                    const v = e.target.value;
                    if (v === '' || parseFloat(v) >= 1) setLiabilities(v);
                  }}
                />
              </div>
            </div>
            <span className="nw-calc-equals">=</span>
            <div className="nw-calc-field nw-calc-result-field">
              <label style={{ color: '#667eea' }}>Net Worth</label>
              <p className={`nw-result-value ${hasInput ? (netWorth >= 0 ? 'positive' : 'negative') : ''}`}>
                {hasInput ? formatCurrency(netWorth) : '—'}
              </p>
            </div>
          </div>
        </div>

        {/* 3. Example */}
        <div className="nw-example">
          <h3>A Simple Example</h3>
          <div className="nw-example-grid">
            <div className="nw-example-col">
              <h4 className="text-green">Assets</h4>
              <div className="nw-example-row"><span>Cash and savings</span><span>$40,000</span></div>
              <div className="nw-example-row"><span>Retirement and investments</span><span>$210,000</span></div>
              <div className="nw-example-row"><span>Home equity</span><span>$150,000</span></div>
              <div className="nw-example-row total"><span>Total Assets</span><span>$400,000</span></div>
            </div>
            <div className="nw-example-col">
              <h4 className="text-red">Liabilities</h4>
              <div className="nw-example-row"><span>Mortgage balance</span><span>$220,000</span></div>
              <div className="nw-example-row"><span>Student loans and other debt</span><span>$30,000</span></div>
              <div className="nw-example-row total"><span>Total Liabilities</span><span>$250,000</span></div>
            </div>
            <div className="nw-example-result">
              Net Worth: <strong>$150,000</strong>
            </div>
          </div>
          <p className="nw-example-note">
            On its own, this number doesn't define success or failure — what matters is the direction it moves over time.
          </p>
        </div>

        {/* 4. Download — take action for long-term tracking */}
        <div className="nw-actions">
          <div className="nw-content-card">
            <h3>Track It Long-Term</h3>
            <p style={{ color: '#94a3b8', marginBottom: '1rem', lineHeight: '1.6' }}>
              Download the net worth tracking template to record your accounts and balances over time. Update it monthly or quarterly to see your trajectory.
            </p>
            {error && (
              <p style={{ color: '#f87171', marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</p>
            )}
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="btn-primary"
              style={{ cursor: downloading ? 'wait' : 'pointer' }}
            >
              {downloading ? 'Preparing Download...' : 'Download Net Worth Template'}
            </button>
          </div>
          <div className="nw-content-card">
            <h3>Watch the Walkthrough</h3>
            <p style={{ color: '#94a3b8', marginBottom: '1rem', lineHeight: '1.6' }}>
              See how to set up the template, organize your accounts, and update balances over time.
            </p>
            {!playingVideo ? (
              <button
                onClick={handleWatchVideo}
                className="btn-primary"
                style={{ cursor: 'pointer' }}
              >
                Watch Instructions
              </button>
            ) : videoUrl ? (
              <div style={{ marginTop: '1rem' }}>
                <video
                  controls
                  autoPlay
                  style={{ width: '100%', borderRadius: '8px' }}
                  src={videoUrl}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            ) : null}
          </div>
        </div>

        <div className="nw-two-col">
          <div className="nw-content-card">
            <h3>What the Tracker Helps You Do</h3>
            <ul>
              <li>Establish a clear financial baseline</li>
              <li>Track progress consistently over time</li>
              <li>See how earning, saving, investing, and debt interact</li>
              <li>Identify imbalances before they become constraints</li>
              <li>Anchor long-term decisions in reality rather than guesswork</li>
            </ul>
          </div>
          <div className="nw-content-card">
            <h3>Designed for Consistent Use</h3>
            <p style={{ color: '#94a3b8', lineHeight: '1.7' }}>
              This tracker is intentionally straightforward. It does not require daily updates or complex assumptions. Many users update it monthly or quarterly, using it as a reference point during periodic reviews.
            </p>
            <p style={{ color: '#94a3b8', lineHeight: '1.7', marginTop: '0.75rem' }}>
              Paired with the Playbook and Lifecycle tools, the Net Worth Tracker helps translate long-term thinking into observable progress — without turning personal finance into a constant project.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};
